import {
	Box,
	Button,
	ButtonGroup,
	Divider,
	Icon,
	IconButton,
	List,
	ListItem,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Popover,
	Skeleton,
	Stack,
	Typography,
} from "@mui/material";
import { blue, grey, lightBlue } from "@mui/material/colors";
import { GlobalState } from "context/GlobalContext";
import { ReportsState } from "context/ReportsContext";
import TableDisplay from "./TableDisplay/TableDisplay";
import { useEffect, useState } from "react";
import {
	achievedColumns,
	blueRenderCell,
	blueShade,
	brandColumn,
	brandLevelTableColumns,
	channelColumn,
	daysLeftColumn,
	defBqRowData,
	defSqlRowData,
	fetchAOPPageDataFromScratch,
	generateAopDataCacheKey,
	prepareAllAopTableDataBasedOnCombinedData,
	projectionColumns,
	reportModes,
	setBqDataInRow,
	setAopProjectionDatainRow,
	sqlDataUpdateColumnsMap,
	staticBrandWiseData,
	staticChannelWiseData,
	staticCombinedData,
	unitRow,
	getFileSignedUrl,
	displayRenderCellWithInfoToolTip,
} from "./data";
import moment from "moment";
import ProjectionEditModal from "./ProjectionEditModal/ProjectionEditModal";
import { converAttrToDatagridCol, getColDefaultValue, selectOptKeyName } from "data/globalData";
import MonthYearSelectorModal from "./MonthYearSelectionModal/MonthYearSelectionModal";
import axios from "axios";
import { multiSort } from "utils/multiColumnSort";
import NsvTable from "./Tables/NsvTable/NsvTable";
import GpTable from "./Tables/GpTable/GpTable";
import BlackBorderTable from "./Tables/TableLayouts/BlackBorderTable/BlackBorderTable";
import ProjectionTable from "./Tables/ProjectionTable/ProjectionTable";
import CustomDateRangePicker from "components/Reports/ReusableComponents/CustomDateRangePicker/CustomDateRangePicker";
import RenderInputField from "components/Reports/ReusableComponents/RenderInputField/RenderInputField";
import { startOfMonth, endOfMonth,startOfDay } from "date-fns";
import { projectionTableColumns } from "./Tables/ProjectionTable/data";
import { filterArrOfObjByMultipleKeys } from "utils/ArrayHelpers";
import { services } from "data/authData";
import parseErrMsgFromError from "utils/errorFunctions/parseErrorMessageFromError";
import { FileDownload, ImportExportRounded, MoreVertRounded } from "@mui/icons-material";
import { exportStyledExcelMultipleSheetsWithTables, exportStyledExcelMultipleTables } from "utils/exportStyledExcel";
import dayjs, { Dayjs } from "dayjs";
import { nsvTableColumns } from "./Tables/NsvTable/data";
import { gpWithChMarginTableColumns, gpWithoutChMarginTableColumns } from "./Tables/GpTable/data";
import { objectShallowCompare } from "@mui/x-data-grid/hooks/utils/useGridSelector";

const getFinancialQuarterRange = (year, quarter) => {
	let startMonth;
	switch (quarter) {
		case 1: // Q1: Apr–Jun
			startMonth = 3;
			break;
		case 2: // Q2: Jul–Sep
			startMonth = 6;
			break;
		case 3: // Q3: Oct–Dec
			startMonth = 9;
			break;
		case 4: // Q4: Jan–Mar (of next calendar year)
			startMonth = 0;
			year += 1;
			break;
		default:
			throw new Error("Invalid quarter");
	}
	const start = startOfMonth(new Date(year, startMonth, 1));
	const end = endOfMonth(new Date(year, startMonth + 2, 1));
	return { start_date: start, end_date: end };
};

const getFYYear = () => {
    const today = new Date();
    // FY starts in April → month index 3
    return today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
};

const currentMonthChange = () => {
	const now = new Date();
	return { start_date: startOfMonth(now), end_date: endOfMonth(now) };
};

const dateStaticRanges = [
	{
    label: "Current Month",
    range: () => {
        const now = new Date();
        return {
            startDate: startOfMonth(now),
            endDate: endOfMonth(now)
        };
    },
    isSelected: (range) => {
        const now = new Date();

        const rStart = new Date(range?.startDate);
        const rEnd = new Date(range?.endDate);

        return (
            rStart.getTime() === startOfMonth(now).getTime() &&
            rEnd.getTime() === endOfMonth(now).getTime()
        );
    },
},

	{
		label: "Q1 (Apr - Jun)",
		range: () => {
            const fy = getFYYear();
            const start = startOfDay(new Date(fy, 3, 1));   // April 1
            const end   = startOfDay(new Date(fy, 5, 30));  // June 30
            return { startDate: start, endDate: end };
        },
        isSelected: (range) => {
            const fy = getFYYear();
            const start = startOfDay(new Date(fy, 3, 1)).getTime();
            const end   = startOfDay(new Date(fy, 5, 30)).getTime();
            return range.startDate.getTime() === start && range.endDate.getTime() === end;
        },
	},

	{
		label: "Q2 (Jul - Sep)",
		range: () => {
            const fy = getFYYear();
            const start = startOfDay(new Date(fy, 6, 1));   // July 1
            const end   = startOfDay(new Date(fy, 8, 30));  // September 30
            return { startDate: start, endDate: end };
        },
        isSelected: (range) => {
            const fy = getFYYear();
            const start = startOfDay(new Date(fy, 6, 1)).getTime();
            const end   = startOfDay(new Date(fy, 8, 30)).getTime();
            return range.startDate.getTime() === start && range.endDate.getTime() === end;
        },
	},

	{
		label: "Q3 (Oct - Dec)",
		range: () => {
					const fy = getFYYear();
					const start = startOfDay(new Date(fy, 9, 1));   // Oct 1
					const end   = startOfDay(new Date(fy, 11, 31)); // Dec 31
					return { startDate: start, endDate: end };
				},
				isSelected: (range) => {
					const fy = getFYYear();
					const start = startOfDay(new Date(fy, 9, 1)).getTime();
					const end   = startOfDay(new Date(fy, 11, 31)).getTime();
					return range.startDate.getTime() === start && range.endDate.getTime() === end;
				},
	},

	{
		label: "Q4 (Jan - Mar)",
		range: () => {
            const fy = getFYYear();
            const start = startOfDay(new Date(fy + 1, 0, 1)); // Jan 1
            const end   = startOfDay(new Date(fy + 1, 2, 31)); // Mar 31
            return { startDate: start, endDate: end };
        },
        isSelected: (range) => {
            const fy = getFYYear();
            const start = startOfDay(new Date(fy + 1, 0, 1)).getTime();
            const end   = startOfDay(new Date(fy + 1, 2, 31)).getTime();
            return range.startDate.getTime() === start && range.endDate.getTime() === end;
        },
	},
];
const AopTracker = () => {
	const { setSnackBarOpen, setSnackBar, snackBar, LoadingOpen, setLoadingOpen, getAppCache, addToAppCache } = GlobalState();
	const [brandWiseData, setBrandWiseData] = useState(staticBrandWiseData);
	const [pageLoading, setPageLoading] = useState(false);
	const [channelWiseData, setChannelWiseData] = useState(staticChannelWiseData);
	const [combinedData, setCombinedData] = useState(staticCombinedData);
	const [projectionEditModal, setProjectionEditModal] = useState(false);
	const [monthYearModal, setMonthYearModal] = useState(false);
	const [selectedMonth, setSelectedMonth] = useState(+moment().format("M") - 1);
	const [selectedYear, setSelectedYear] = useState(+moment().format("YYYY"));
	const [daysLeft, setDaysLeft] = useState(0);
	const [pageData, setPageData] = useState();
	const [reportMode, setReportMode] = useState(reportModes.FINANCE.key);
	const [filterData, setFilterData] = useState({});
	const [filterDropdowns, setFilterDropdowns] = useState({});
	const [anchorEl, setAnchorEl] = useState(null);

	const open = Boolean(anchorEl);
	const handleCloseMorePopover = () => {
		setAnchorEl(null);
	};
	const handleOpenMorePopover = (event) => {
		setAnchorEl(event.currentTarget);
	};

	const defaultDateRange = {
		...currentMonthChange(),

	};
	const [dateRange, setDateRange] = useState({ ...defaultDateRange });
	// console.log(dateRange, "skjrfhskjdhfwe");

	const loadData = async () => {
		const cacheKey = generateAopDataCacheKey(dateRange);
		console.log(cacheKey, "looking for cache");
		const cached = getAppCache(cacheKey);
		const currentMonth = dayjs().get("M");
		if (cached) {
			const startMonth = dayjs(dateRange.start_date).get("M");
			const endMonth = dayjs(dateRange.end_date).get("M");
			console.log("Start date:", dayjs(dateRange.start_date).format(), "Month:", dayjs(dateRange.start_date).get("M"));
			console.log("End date:", dayjs(dateRange.end_date).format(), "Month:", dayjs(dateRange.end_date).get("M"));
			console.log("Cur Month:", dayjs().format(), "Month:", dayjs().get("M"));
			if (startMonth !== currentMonth && endMonth !== currentMonth) {
				return cached;
			}
			// Proceed with fresh logic here
		}
		const data = await fetchAOPPageDataFromScratch(dateRange, addToAppCache, getAppCache);
		addToAppCache(cacheKey, data);
		return data;
	};
	const handlePageDataExport = async () => {
		const combinedColumns = [
			brandColumn,
			channelColumn,
			...Object.values(nsvTableColumns),
			...Object.values(reportMode === reportModes.FINANCE.key ? gpWithoutChMarginTableColumns : gpWithChMarginTableColumns).map(
				(row) => ({ ...row, name: `(GP) ${row.name}` })
			),
			daysLeftColumn,
			...Object.values(projectionTableColumns),
		];

		const brandWiseDataForTalbe = (raw = false) => {
			let columns = [...combinedColumns.filter((row) => row.key !== channelColumn.key)];
			if (raw)
				columns = columns?.map((colObj) => {
					const col = { ...colObj };

					if (typeof col?.valueGetter === "function") {
						let valGetter = col?.valueGetter;
						let fun = (row) => valGetter(row, raw);
						col["valueGetter"] = fun;
					}
					return col;
				});

			return {
				title: `Brand Wise (${
					reportMode === reportModes.FINANCE.key ? "Gp Without Channel Margin" : "Gp With Channel Margin"
				})`,
				columns: combinedColumns.filter((row) => row.key !== channelColumn.key),
				rows: brandWiseData.map((row) => ({ ...row, [daysLeftColumn.key]: daysLeft })),
			};
		};
		const channelWiseDataForTalbe = (raw = false) => {
			let columns = [...combinedColumns.filter((row) => row.key !== brandColumn.key)];
			if (raw)
				columns = columns?.map((colObj) => {
					const col = { ...colObj };

					if (typeof col?.valueGetter === "function") {
						let valGetter = col?.valueGetter;
						let fun = (row) => valGetter(row, raw);
						col["valueGetter"] = fun;
					}
					return col;
				});
			return {
				title: `Channel Wise (${
					reportMode === reportModes.FINANCE.key ? "Gp Without Channel Margin" : "Gp With Channel Margin"
				})`,
				columns,
				rows: channelWiseData.map((row) => ({ ...row, [daysLeftColumn.key]: daysLeft })),
			};
		};
		const combinedDataForTalbe = (raw = false) => {
			let columns = [...combinedColumns];

			if (raw)
				columns = columns?.map((colObj) => {
					const col = { ...colObj };

					if (typeof col?.valueGetter === "function") {
						let valGetter = col?.valueGetter;
						let fun = (row) => valGetter(row, raw);
						col["valueGetter"] = fun;
					}
					return col;
				});
			return {
				title: `Combined (${reportMode === reportModes.FINANCE.key ? "Gp Without Channel Margin" : "Gp With Channel Margin"})`,
				columns,
				rows: combinedData.map((row) => ({ ...row, [daysLeftColumn.key]: daysLeft })),
			};
		};

		const sheets = [
			{
				sheetName: "AOP Data",
				tables: [
					{
						...brandWiseDataForTalbe(false),
					},
					{
						...channelWiseDataForTalbe(false),
					},

					{
						...combinedDataForTalbe(false),
					},
				],
			},
			{
				sheetName: "AOP Data (Raw)",
				tables: [
					{
						...brandWiseDataForTalbe(true),
					},
					{
						...channelWiseDataForTalbe(true),
					},

					{
						...combinedDataForTalbe(true),
					},
				],
			},
		];
		if (combinedData) {
			exportStyledExcelMultipleSheetsWithTables({
				filename: `AopData_${reportMode === reportModes.FINANCE.key ? "Without_CM" : "With_CM"}_${dayjs(
					dateRange?.startDatertDate
				).format("DD_MMM_YY")}_till_${dayjs(dateRange?.end_date).format("DD_MMM_YY")}.xlsx`,
				sheets,
			});
			handleCloseMorePopover();
		}
	};
	const handleDocumentationDownload = async () => {
		try {
			setLoadingOpen(true);
			const fileUrl = await getFileSignedUrl(`revenue_aop_vs_actual/documentation/latest/aopLogics.pdf`);
			if (fileUrl) {
				window.open(fileUrl, "_blank");
				handleCloseMorePopover();
				setSnackBarOpen(true);
				setSnackBar({ ...snackBar, message: "File Downloaded Successfully", severity: "error" });
				setLoadingOpen(false);
			}
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({ ...snackBar, message: parseErrMsgFromError(error), severity: "error" });
			setLoadingOpen(false);
		}
	};

	const fetch = async () => {
		try {
			setPageLoading(true);
			const { filterDropdowns: filters, fullData } = await loadData();
			setFilterDropdowns(filters);
			setPageData(fullData);
			const { brandTableData, channelTableData, combinedTableData } = prepareAllAopTableDataBasedOnCombinedData(fullData);
			setBrandWiseData(brandTableData);
			setChannelWiseData(channelTableData);
			setCombinedData(combinedTableData);
			setPageLoading(false);
		} catch (error) {
			setPageLoading(false);
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: parseErrMsgFromError(error),
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
			setLoadingOpen(false);
		}
	};

	useEffect(() => {
		if (!dateRange || !dateRange?.end_date || !dateRange?.start_date) {
			setDaysLeft(0);
			return;
		}
		fetch();

		const end_date = moment(dateRange.end_date).endOf("day");
		const today = moment().startOf("day");

		const days = end_date.diff(today, "days");

		setDaysLeft(days > 0 ? days : 0);
	}, [dateRange]);

	// handle Filter Change
	useEffect(() => {
		if (Object.keys(filterData).length && pageData?.length) {
			const filteredData = filterArrOfObjByMultipleKeys(pageData, filterData);
			const { brandTableData, channelTableData, combinedTableData } = prepareAllAopTableDataBasedOnCombinedData(filteredData);
			console.log(combinedTableData, "combined_data ");
			setBrandWiseData(brandTableData);
			setChannelWiseData(channelTableData);
			setCombinedData(combinedTableData);
		} else if (pageData?.length && !Object.keys(filterData).length) {
			const { brandTableData, channelTableData, combinedTableData } = prepareAllAopTableDataBasedOnCombinedData(pageData);
			setBrandWiseData(brandTableData);
			setChannelWiseData(channelTableData);
			setCombinedData(combinedTableData);
		}
	}, [filterData]);

	const handleMonthYearChange = (month, year) => {
		setSelectedMonth(month);
		setSelectedYear(year);
	};

	const handleMonthYearModalOpen = () => {
		setMonthYearModal(true);
	};

	return (
		<Stack position="relative" margin="auto" spacing={2} width="95%" mt={3}>
			<Typography variant="h5" mt={-2}>
				{services.aop.name}
			</Typography>
			<Box
				display="flex"
				flexWrap="wrap"
				width="100%"
				alignItems="center"
				rowGap="5px"
				position="sticky"
				top="62px"
				left="0%"
				zIndex={1}
				bgcolor="white"
				boxSizing="border-box"
				boxShadow={3}
				borderRadius="3px"
				columnGap={"20px"}
				p={"6px 12px"}>
				{/* <Divider sx={{ width: "100%" }} /> */}
				<Typography variant="body1" color={grey[700]}>
					Filters
				</Typography>
				<Box minWidth={200} width="15%">
					<CustomDateRangePicker
						dateRange={dateRange}
						setDateRange={setDateRange}
						// defaultDateRange={defaultDateRange}
						staticRanges={dateStaticRanges}
						// maxDate={new Date()}
					/>
				</Box>
				{[brandColumn, channelColumn].map((column) => {
					return (
						<Box minWidth={150} width="15%">
							<RenderInputField
								column={{
									...column,
									[selectOptKeyName]: {
										list: filterDropdowns?.[column.key] ?? [],
										getOptionLabel: (row) => ({ name: row, key: row }),
									},
								}}
								data={filterData}
								setData={setFilterData}
								selectOptObjName={selectOptKeyName}
								width={"100%"}
								longWidth={"100%"}
								// disabled={disabled}
								// error={errObj?.[column.key] ? true : false}
								// setError={setErrorObj}
								// dependencyColData={}
								disableLabel
							/>
						</Box>
					);
				})}
				<Box flexGrow={1} display="flex" justifyContent="flex-end">
					<ButtonGroup disableElevation variant="contained">
						{Object.values(reportModes).map((row) => {
							return (
								<Button
									onClick={() => {
										if (reportMode !== row["key"]) setReportMode(row["key"]);
									}}
									variant={reportMode === row["key"] ? "contained" : "outlined"}
									sx={{ p: "7px 20px", textTransform: "capitalize" }}>
									{row["name"]}
								</Button>
							);
						})}
					</ButtonGroup>
					<Box>
						<IconButton aria-describedby={"aop_popover"} onClick={handleOpenMorePopover} marginRight="20px" sx={{ ml: 1 }}>
							<MoreVertRounded />
						</IconButton>
						<Popover
							id={"aop_popover"}
							open={open}
							anchorEl={anchorEl}
							onClose={handleCloseMorePopover}
							anchorOrigin={{
								vertical: "bottom",
								horizontal: "right",
							}}
							transformOrigin={{
								vertical: "top",
								horizontal: "right",
							}}>
							<List disablePadding>
								<ListItemButton
									className="groupbyclass"
									onClick={handlePageDataExport}
									sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
									<FileDownload fontSize="small" sx={{ mr: 1 }} />
									<ListItemText>Export Data</ListItemText>
								</ListItemButton>
								<ListItemButton
									className="groupbyclass"
									onClick={handleDocumentationDownload}
									sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
									<FileDownload fontSize="small" sx={{ mr: 1 }} />
									<ListItemText>Document</ListItemText>
								</ListItemButton>
							</List>
						</Popover>
					</Box>
				</Box>
				{/* <Divider sx={{ width: "100%" }} /> */}
			</Box>
			<Box display="flex" justifyContent="space-between">
				<Typography fontStyle="italic" variant="body2">
					* Value Achieved is till date {moment().format("DD-MM-YYYY")}
				</Typography>
				<Box display="flex" justifyContent="center">
					<Typography variant="body1" fontWeight={600}>
						Date:
					</Typography>
					<Typography variant="body1" textAlign="right" pr={1}>
						{" " + moment().format("DD-MM-YYYY")}
					</Typography>
				</Box>
			</Box>
			{/* tables */}
			{!pageLoading ? (
				<>
					{/* BRAND */}
					<Box display="flex" minHeight={200} flexGrow={1} sx={{ overflowX: "scroll" }}>
						<Typography sx={{ writingMode: "sideways-lr", textAlign: "center" }}>BRANDWISE</Typography>
						<Box ml={1} sx={{ position: "sticky", left: 0, top: "5px" }}>
							<Typography textAlign="center" height="24px" visibility="hidden" />
							<Typography textAlign="center" height="24px" visibility="hidden" />
							<div style={{ background: "white", flexGrow: 1 }}>
								<TableDisplay
									columns={[{ ...brandColumn, renderCell: displayRenderCellWithInfoToolTip("LAST_DATE") }]}
									rows={[unitRow, ...brandWiseData]}
									rowHeight="30px"
									columnWidth={"130px"}
								/>
							</div>
						</Box>
						<Stack>
							<Typography textAlign="center" sx={{ background: grey[500] }} height="24px" width="100%">
								ACHIEVED
							</Typography>
							<Box display="flex" flexGrow={1}>
								<NsvTable tableRows={brandWiseData} setTableRows={setBrandWiseData} />
								<GpTable tableRows={brandWiseData} setTableRows={setBrandWiseData} mode={reportMode} />
							</Box>
						</Stack>
						<Box width="100px">
							<Typography textAlign="center" height="24px" visibility="hidden" />
							<Typography textAlign="center" height="24px" visibility="hidden" />
							<TableDisplay
								columns={[daysLeftColumn]}
								rows={[unitRow, ...brandWiseData.map((row) => ({ [daysLeftColumn.key]: daysLeft }))]}
								rowHeight="30px"
							/>
						</Box>
						<Box flexGrow={1}>
							<Typography textAlign="center" height="24px" visibility="hidden" />
							<Typography textAlign="center" sx={{ background: blueShade }} height="24px">
								PROJECTION
							</Typography>
							<ProjectionTable tableRows={brandWiseData} setTableRows={setBrandWiseData} />
						</Box>
					</Box>
					{/* Channel */}
					<Box display="flex" minHeight={200} flexGrow={1} sx={{ overflowX: "scroll" }}>
						<Typography sx={{ writingMode: "sideways-lr", textAlign: "center" }}>CHANNELWISE</Typography>
						<Box ml={1} sx={{ position: "sticky", left: 0, top: "5px" }}>
							<Typography textAlign="center" height="24px" visibility="hidden" />
							<Typography textAlign="center" height="24px" visibility="hidden" />
							<Box sx={{ background: "white", flexGrow: 1 }}>
								<TableDisplay
									columns={[{ ...channelColumn, renderCell: displayRenderCellWithInfoToolTip("LAST_DATE") }]}
									rows={[unitRow, ...channelWiseData]}
									rowHeight="30px"
								/>
							</Box>
						</Box>

						<Stack>
							<Typography textAlign="center" sx={{ background: grey[500] }} height="24px" width="100%">
								ACHIEVED
							</Typography>
							<Box display="flex" flexGrow={1}>
								<NsvTable tableRows={channelWiseData} setTableRows={setChannelWiseData} />
								<GpTable tableRows={channelWiseData} setTableRows={setChannelWiseData} mode={reportMode} />
							</Box>
						</Stack>
						<Box width="100px">
							<Typography textAlign="center" height="24px" visibility="hidden" />
							<Typography textAlign="center" height="24px" visibility="hidden" />
							<TableDisplay
								columns={Object.values([daysLeftColumn])}
								rows={[unitRow, ...channelWiseData.map((row) => ({ [daysLeftColumn.key]: daysLeft }))]}
								rowHeight="30px"
							/>
						</Box>
						<Stack>
							<Typography textAlign="center" height="24px" visibility="hidden" />
							<Typography textAlign="center" sx={{ background: blueShade }} height="24px">
								PROJECTION
							</Typography>
							<ProjectionTable tableRows={channelWiseData} setTableRows={setChannelWiseData} />
						</Stack>
					</Box>

					{/* Projected */}
					<Box width="100%">
						<Box display="flex" width="100%" justifyContent="space-between" pt={2} pb={1}>
							<Typography variant="h6">PROJECTED</Typography>
							<Box>
								<Button variant="contained" onClick={() => setProjectionEditModal(true)} disabled={daysLeft <= 0}>
									Edit
								</Button>
							</Box>
						</Box>
						<Box display="flex" alignItems="center" minHeight={200} width="100%" sx={{ overflowX: "scroll" }}>
							<Box flexGrow={1} sx={{ position: "sticky", left: 0, top: "5px" }}>
								<Typography textAlign="center" height="24px" visibility="hidden" />
								<Typography textAlign="center" height="24px" visibility="hidden" />
								<BlackBorderTable
									disableBorder
									sx={{
										flexGrow: 1,
										p: 0,
										border: 0,
										background: "white",
									}}>
									<TableDisplay
										columns={[
											brandColumn,
											{ ...channelColumn, renderCell: displayRenderCellWithInfoToolTip("LAST_DATE") },
										]}
										rows={[unitRow, ...combinedData]}
										rowHeight="30px"
										rowSpanColumns={{ BRAND: true }}
										customGroupBorderBottom={`3px solid ${grey[300]}`}
										// disableTableBorder
									/>
								</BlackBorderTable>
							</Box>
							<Box display="flex" flexWrap="wrap">
								<Typography textAlign="center" sx={{ background: grey[500] }} height="24px" width="100%">
									ACHIEVED
								</Typography>
								<Box display="flex" flexGrow={1}>
									<NsvTable
										tableRows={combinedData}
										setTableRows={setCombinedData}
										rowSpanColumns={{ BRAND: true }}
										customGroupBorderBottom={`3px solid ${grey[300]}`}
									/>
									<GpTable
										tableRows={combinedData}
										setTableRows={setCombinedData}
										rowSpanColumns={{ BRAND: true }}
										customGroupBorderBottom={`3px solid ${grey[300]}`}
										mode={reportMode}
									/>
								</Box>
							</Box>
							<Stack width="100px">
								<Typography textAlign="center" height="24px" visibility="hidden" />
								<Typography textAlign="center" height="24px" visibility="hidden" />
								<BlackBorderTable disableBorder sx={{ border: "0px", p: "0px" }}>
									<TableDisplay
										columns={Object.values([daysLeftColumn])}
										rows={[unitRow, ...combinedData.map((row) => ({ ...row, [daysLeftColumn.key]: daysLeft }))]}
										rowHeight="30px"
										rowSpanColumns={{ BRAND: true }}
										customGroupBorderBottom={`3px solid ${grey[300]}`}
										disableTableBorder
									/>
								</BlackBorderTable>
							</Stack>
							<Stack>
								<Typography textAlign="center" height="24px" visibility="hidden" />
								<Typography textAlign="center" sx={{ background: blueShade }} height="24px">
									PROJECTION
								</Typography>
								<ProjectionTable
									tableRows={combinedData}
									setTableRows={setCombinedData}
									customGroupBorderBottom={`3px solid ${grey[300]}`}
									rowSpanColumns={{ BRAND: true }}
								/>
							</Stack>
						</Box>
					</Box>
				</>
			) : (
				<Stack width="100%" spacing={2}>
					<Skeleton variant="rounded" animation="pulse" width="100%" height={280} />
					<Skeleton variant="rounded" animation="pulse" width="100%" height={340} />
					<Skeleton variant="rounded" animation="pulse" width="100%" height={400} />
				</Stack>
			)}

			<MonthYearSelectorModal
				modalOpen={monthYearModal}
				setModalOpen={setMonthYearModal}
				saveFunction={handleMonthYearChange}
				month={selectedMonth}
				year={selectedYear}
			/>
			<ProjectionEditModal
				modalOpen={projectionEditModal}
				setModalOpen={setProjectionEditModal}
				dataArr={combinedData}
				setDataArr={setCombinedData}
				saveCallback={() => fetch()}
			/>
		</Stack>
	);
};

export default AopTracker;
